import StateError from './StateError';
import { setWasCalled, wasCalled } from './wasCalledUtils';

/**
 * Decorated method will throw if called more than once.
 *
 * @example
 * ```ts
 * class Initializable {
 *   @atMostOnce
 *   init() {
 *     // ...
 *   }
 * }
 *
 * const initializable = new Initializable();
 * initializable.init(); // OK
 * initializable.init(); // throws
 * ```
 */
export default function atMostOnce<M extends string>(
  target: {
    [N in M]: (...args: any[]) => any;
  },
  propertyKey: M,
  descriptor: TypedPropertyDescriptor<(...args: any[]) => any>
) {
  // The original decorated method
  const originalMethod = descriptor.value;

  // Replace the decorated method's definition with one that throws if called more than once
  descriptor.value = function(...args: any[]) {
    // Throw an error if the decorated method has been invoked
    if (wasCalled(this, propertyKey)) {
      throw new StateError(`${target.constructor.name}.${propertyKey} may be called at most once.`);
    }

    // The method hasn't been invoked yet; set the "was called" flag and invoke the original method
    setWasCalled(this, propertyKey);
    return originalMethod && originalMethod.apply(this, args);
  };
}
